import { useParams, Link } from 'react-router-dom';
import { articles, type Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { useEffect, useState } from 'react';
import SEOSchema from '@/components/feature/SEOSchema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug);
    if (found) {
      setArticle(found);
      setRelatedArticles(
        articles
          .filter((a) => a.category === found.category && a.slug !== found.slug)
          .slice(0, 3)
      );
      document.title = `${found.title} - 无码开发平台`;
    } else {
      setArticle(null);
      setRelatedArticles([]);
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-background-50">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  return (
    <>
      <SEOSchema
        pageType="article"
        pageTitle={`${article.title} - 无码开发平台`}
        pageDescription={article.summary}
        canonicalPath={`/article/${article.slug}`}
        breadcrumbs={[
          { name: '首页', path: '/' },
          ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
          { name: article.title, path: `/article/${article.slug}` },
        ]}
        articleData={{
          title: article.title,
          author: article.author,
          datePublished: article.date,
          category: category?.name,
        }}
      />
      <main className="min-h-screen bg-background-50">
        <article>
          <section className="pt-28 pb-12 px-6 md:px-10 bg-background-100 border-b border-background-200/70">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                {category && (
                  <>
                    <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      {category.name}
                    </Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </>
                )}
                <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                    {article.author.charAt(0)}
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span>{article.date}</span>
                <span>&middot;</span>
                <span>{article.readTime}</span>
                {category && (
                  <>
                    <span>&middot;</span>
                    <Link to={`/category/${category.slug}`} className="text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                      {category.name}
                    </Link>
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">{tag}</span>
                ))}
              </div>
            </div>
          </section>

          <section className="px-6 md:px-10 py-12">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom text-foreground-800 leading-relaxed">
                {article.content.split('\n').map((line, idx) => {
                  if (line.startsWith('## ')) {
                    return (
                      <h2 key={idx} className="text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                        {line.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (line.startsWith('### ')) {
                    return (
                      <h3 key={idx} className="text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                        {line.replace('### ', '')}
                      </h3>
                    );
                  }
                  if (line.startsWith('- ')) {
                    return (
                      <li key={idx} className="ml-6 text-foreground-700 mb-1 list-disc">
                        {line.replace('- ', '')}
                      </li>
                    );
                  }
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return (
                      <p key={idx} className="font-semibold text-foreground-900 mt-6 mb-2">
                        {line.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  if (line.trim() === '') {
                    return <div key={idx} className="h-3"></div>;
                  }
                  return (
                    <p key={idx} className="text-foreground-700 leading-relaxed mb-3 text-base">
                      {line}
                    </p>
                  );
                })}
              </div>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="px-6 md:px-10 py-12 bg-background-100 border-t border-background-200/70">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-600 line-clamp-2">{ra.summary}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}